import { useCallback, useEffect, useState, useRef } from "react";
import { Button, Input, Spin, message as antdMessage } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { DownOutlined, SearchOutlined, UpOutlined } from "@ant-design/icons";
import { findChatSearchMatches } from "../../utils/chatSearch";
import {
  resetChatSearch,
  setChatSearchResults,
  addChatSearchResults,
  setChatSearchIndex,
  setIsSearchLoading,
  setClearedMessages,
  setHasMorePages,
  // getChatData,
  // setChatPage,
} from "../../redux/slices/chatSlice";
import "./searchBar.scss";
import ChatService from "../../services/api/chatService";
import { getCurrentDate } from "../../utils/helpers";
// import { useNavigate } from "react-router-dom";
const START_DATE = "2025-01-15";
const SearchBar = ({ isOpen }) => {
  const [searchText, setSearchText] = useState("");
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  // const navigate = useNavigate;
  const chatService = useRef(new ChatService());
  const searchCancelledRef = useRef(false);
  const {
    messages,
    searchMatches,
    searchIndex,
    isSearchLoading,
    hasMorePages,
    chatPage,
  } = useSelector((state) => state.chatReducer);
  const handleClearSearch = useCallback(() => {
    setSearchText("");
    searchCancelledRef.current = true;
    dispatch(resetChatSearch());
  }, [dispatch]);
  const runSearch = useCallback(async () => {
    const trimmedText = searchText.trim();
    if (!trimmedText || isSearchLoading) {
      if (!trimmedText) {
        handleClearSearch();
      }
      return;
    }
    searchCancelledRef.current = true;
    await new Promise((resolve) => setTimeout(resolve, 50));
    searchCancelledRef.current = false;
    dispatch(setIsSearchLoading(true));
    try {
      const normalizedTerm = trimmedText.toLowerCase();
      const currentMatches = findChatSearchMatches(messages, normalizedTerm);
      dispatch(
        setChatSearchResults({
          searchTerm: trimmedText,
          matches: currentMatches,
        })
      );
      if (!searchCancelledRef.current && currentMatches.length === 0) {
        antdMessage.info("No matches found");
      }
    } catch (error) {
      if (!searchCancelledRef.current) {
        console.error("Error during search:", error);
        antdMessage.info("An error occurred during search");
      }
    } finally {
      if (!searchCancelledRef.current) {
        dispatch(setIsSearchLoading(false));
      }
    }
  }, [
    dispatch,
    handleClearSearch,
    isSearchLoading,
    messages,
    searchText,
  ]);
  const onSearchClick = useCallback(() => {
    runSearch();
  }, [runSearch]);
  const onClickNextResult = () => {
    const newIndex = searchIndex + 1;
    if (newIndex >= searchMatches.length) return;
    dispatch(setChatSearchIndex(newIndex));
  };
  const onClickPreviousResult = () => {
    const newIndex = searchIndex - 1;
    if (newIndex < 0) return;
    dispatch(setChatSearchIndex(newIndex));
  };
  useEffect(() => {
    if (!isOpen) {
      searchCancelledRef.current = true;
      handleClearSearch();
    } else {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
    return () => {
      searchCancelledRef.current = true;
    };
  }, [handleClearSearch, isOpen]);
  if (!isOpen) return null;
  return (
    <div className="search-container">
      <Input
        ref={inputRef}
        variant="outlined"
        className="chat-search-input"
        placeholder={"Search your messages"}
        allowClear
        value={searchText}
        onChange={(e) => {
          const value = e.target.value;
          setSearchText(value);
          if (!value.trim()) {
            handleClearSearch();
          }
        }}
        onPressEnter={onSearchClick}
      />

      <Button
        onClick={onSearchClick}
        style={{ marginLeft: "10px" }}
        disabled={!searchText.trim().length || isSearchLoading}
      >
        <div style={{ display: "flex", gap: "5px" }}>
          {isSearchLoading ? <Spin size="small" /> : <SearchOutlined />}
        </div>
      </Button>

      {searchMatches.length > 0 && (
        <>
          <div className="chat-search-index-text">
            {searchIndex + 1}/{searchMatches.length}
          </div>

          <Button
            onClick={onClickPreviousResult}
            className="chat-search-up-button"
            disabled={searchIndex === 0}
          >
            <DownOutlined />
          </Button>

          <Button
            onClick={onClickNextResult}
            className="chat-search-down-button"
            disabled={searchIndex === searchMatches.length - 1}
          >
            <UpOutlined />
          </Button>
        </>
      )}
    </div>
  );
};
export default SearchBar;
